<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { storeToRefs } from 'pinia';
import { useAlert } from 'dashboard/composables';
import { useCaptainConfigStore } from 'dashboard/store/captain/preferences';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';

const { t } = useI18n();
const captainConfigStore = useCaptainConfigStore();
const { openai, uiFlags } = storeToRefs(captainConfigStore);

const apiKey = ref('');
const endpoint = ref('');
const isSaving = ref(false);

watch(
  openai,
  value => {
    endpoint.value = value?.endpoint || '';
  },
  { immediate: true }
);

const statusLabel = computed(() => {
  const source = openai.value?.source;
  if (source === 'account') {
    return t('CAPTAIN_SETTINGS.OPENAI.STATUS_ACCOUNT', {
      masked: openai.value?.api_key_masked || '****',
    });
  }
  if (source === 'global') {
    return t('CAPTAIN_SETTINGS.OPENAI.STATUS_GLOBAL', {
      masked: openai.value?.api_key_masked || '****',
    });
  }
  return t('CAPTAIN_SETTINGS.OPENAI.STATUS_NONE');
});

const canClear = computed(() => openai.value?.source === 'account');
const isBusy = computed(() => isSaving.value || uiFlags.value.isFetching);

async function handleSave() {
  if (
    !apiKey.value.trim() &&
    !endpoint.value.trim() &&
    openai.value?.source !== 'account'
  ) {
    useAlert(t('CAPTAIN_SETTINGS.OPENAI.API_KEY_REQUIRED'));
    return;
  }

  isSaving.value = true;
  try {
    const payload = { openai: {} };
    if (apiKey.value.trim()) {
      payload.openai.api_key = apiKey.value.trim();
    }
    payload.openai.endpoint = endpoint.value.trim();
    await captainConfigStore.updatePreferences(payload);
    apiKey.value = '';
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN_SETTINGS.API.ERROR'));
    await captainConfigStore.fetch();
  } finally {
    isSaving.value = false;
  }
}

async function handleClear() {
  isSaving.value = true;
  try {
    await captainConfigStore.updatePreferences({ openai: { clear: true } });
    apiKey.value = '';
    useAlert(t('CAPTAIN_SETTINGS.API.SUCCESS'));
  } catch (error) {
    useAlert(t('CAPTAIN_SETTINGS.API.ERROR'));
    await captainConfigStore.fetch();
  } finally {
    isSaving.value = false;
  }
}
</script>

<template>
  <div
    class="flex flex-col gap-4 p-4 rounded-xl border border-n-weak bg-n-solid-1"
  >
    <div>
      <h4 class="text-sm font-medium text-n-slate-12">
        {{ t('CAPTAIN_SETTINGS.OPENAI.API_KEY_LABEL') }}
      </h4>
      <p class="text-sm text-n-slate-11 mt-0.5">
        {{ t('CAPTAIN_SETTINGS.OPENAI.API_KEY_DESCRIPTION') }}
      </p>
      <p class="text-xs text-n-slate-10 mt-2">
        {{ statusLabel }}
      </p>
    </div>

    <Input
      v-model="apiKey"
      type="password"
      autocomplete="new-password"
      :label="t('CAPTAIN_SETTINGS.OPENAI.API_KEY_INPUT')"
      :placeholder="t('CAPTAIN_SETTINGS.OPENAI.API_KEY_PLACEHOLDER')"
      :disabled="isBusy"
    />

    <Input
      v-model="endpoint"
      type="url"
      :label="t('CAPTAIN_SETTINGS.OPENAI.ENDPOINT_LABEL')"
      :placeholder="t('CAPTAIN_SETTINGS.OPENAI.ENDPOINT_PLACEHOLDER')"
      :message="t('CAPTAIN_SETTINGS.OPENAI.ENDPOINT_HELP')"
      :disabled="isBusy"
    />

    <div class="flex flex-wrap items-center gap-2">
      <Button
        :label="t('CAPTAIN_SETTINGS.OPENAI.SAVE')"
        color="blue"
        size="sm"
        :is-loading="isSaving"
        :disabled="isBusy"
        @click="handleSave"
      />
      <Button
        v-if="canClear"
        :label="t('CAPTAIN_SETTINGS.OPENAI.CLEAR')"
        variant="faded"
        color="slate"
        size="sm"
        :disabled="isBusy"
        @click="handleClear"
      />
    </div>
  </div>
</template>
